import is from '@sindresorhus/is';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

export type JobProfileItem = JobLevel | JobLevelDetails | JobRole | JobRoleDetails;

export type WorkflowStatusType = 'assign-reviewer' | 'is-generating' | 'none' | 'publish' | 'review';

export type ItemWithVersionInfo = {
    readonly actions: {
        readonly canAssignReviewer: boolean;
        readonly canPublish?: boolean;
    };
    readonly profileGenerationInfo?: {
        readonly skillDetectionState?: string | null;
    } | null;
    readonly reviewer?: unknown;
    readonly versionInfo?: {
        readonly isPublished?: boolean;
        readonly isReviewed?: boolean;
    } | null;
};

const isGenerating = (item: ItemWithVersionInfo): boolean => {
    const skillDetectionState = item.profileGenerationInfo?.skillDetectionState;

    return (
        !is.nullOrUndefined(skillDetectionState) &&
        skillDetectionState !== 'finished' &&
        skillDetectionState !== 'failed'
    );
};

const hasNoVersionInfo = (item: ItemWithVersionInfo): boolean => is.nullOrUndefined(item.versionInfo);

const isAlreadyPublished = (item: ItemWithVersionInfo): boolean => item.versionInfo?.isPublished === true;

const isReviewed = (item: ItemWithVersionInfo): boolean => item.versionInfo?.isReviewed === true;

const canPublish = (item: ItemWithVersionInfo): boolean => item.actions.canPublish === true;

const canAssignReviewer = (item: ItemWithVersionInfo): boolean => item.actions.canAssignReviewer === true;

const hasNoReviewer = (item: ItemWithVersionInfo): boolean => is.nullOrUndefined(item.reviewer);

export const getWorkflowStatus = (item: ItemWithVersionInfo): WorkflowStatusType => {
    if (isGenerating(item)) {
        return 'is-generating';
    }

    if (hasNoVersionInfo(item)) {
        return 'none';
    }

    if (isAlreadyPublished(item)) {
        return 'none';
    }

    if (isReviewed(item) && canPublish(item)) {
        return 'publish';
    }

    if (hasNoReviewer(item)) {
        if (canAssignReviewer(item)) {
            return 'assign-reviewer';
        }

        return 'none';
    }

    if (!isReviewed(item) && canAssignReviewer(item)) {
        return 'review';
    }

    return 'none';
};

export const useJobProfileWorkflowStatus = (item: JobProfileItem | undefined): WorkflowStatusType => {
    if (is.nullOrUndefined(item)) {
        return 'none';
    }

    return getWorkflowStatus(item);
};
